import { palette, spaces } from '@kimuno/theme';
import { Box, DialogTitle, Stack } from '@mui/material';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';

import {
	clearTemporaryConversation as clearTemporaryConversationAction,
	setCurrentConversationId as setCurrentConversationIdAction,
} from '../../../actions/aiAssistant.actions';
import mukiLogo from '../../../assets/assistant/muki-logo.svg';
import { routes } from '../../../constants';
import { aiAssistantContext } from '../../../state/AIAssistantContextProvider';
import useAction from '../../../utils/useAction';
import useRouter from '../../../utils/useRouter';
import useScreenSize from '../../../utils/useScreenSize';
import { ActiveView } from '../shared/constants';
import AIAssistantIconButton from './AIAssistantIconButton';

const AIAssistantDialogTitle = () => {
	const { t } = useTranslation();
	const { isMdScreen, isLgScreen } = useScreenSize();
	const isDesktopScreen = isMdScreen || isLgScreen;
	const {
		match: {
			location: { pathname },
		},
	} = useRouter();
	const isReaderPage = pathname.startsWith(`/${routes.reader.root}/`);

	const { closeAssistant, isExtended, toggleExtended, setActiveView } =
		useContext(aiAssistantContext);

	const setCurrentConversationId = useAction(setCurrentConversationIdAction);
	const clearTemporaryConversation = useAction(clearTemporaryConversationAction);

	const handleNewChat = () => {
		setCurrentConversationId({ conversationId: null });
		clearTemporaryConversation();
		setActiveView(isReaderPage ? ActiveView.UNIT_ENTRY : ActiveView.FREE_DRAFT_ENTRY);
	};

	return (
		<DialogTitle
			className="draggable-dialog-title"
			id="draggable-dialog-title"
			data-testid="ai-assistant-dialog-title"
			sx={{
				cursor: isDesktopScreen ? 'move' : 'initial',
				borderBottomWidth: '1px',
				borderBottomStyle: 'solid',
				borderColor: palette.primary.contrast,
				color: palette.text.secondary,
				justifyContent: 'flex-end',
				display: 'flex',
				padding: spaces.md,
			}}
		>
			<Stack direction="row" justifyContent="space-between" alignItems="center" width="100%">
				<Stack
					direction="row"
					gap={spaces.xxs}
					sx={{
						paddingY: 0,
					}}
				>
					<AIAssistantIconButton
						data-testid="ai-assistant-new-chat-button"
						data-icon="edit"
						icon="edit"
						tooltip={t('New chat')}
						onClick={handleNewChat}
						onTouchEnd={handleNewChat}
					/>
				</Stack>
				<Box sx={{ width: 90, height: 40 }} component={mukiLogo} />
				<Stack direction="row" gap={spaces.xxs} sx={{ paddingLeft: isDesktopScreen ? 0 : '44px' }}>
					{isDesktopScreen && (
						<AIAssistantIconButton
							data-testid="ai-assistant-extend-button"
							icon={isExtended ? 'minimize' : 'maximize'}
							tooltip={isExtended ? t('Minimize') : t('Maximize')}
							onClick={toggleExtended}
							onTouchEnd={toggleExtended}
						/>
					)}
					<AIAssistantIconButton
						data-testid="ai-assistant-close-button"
						icon="close"
						tooltip={t('Close')}
						onClick={closeAssistant}
						onTouchEnd={closeAssistant}
					/>
				</Stack>
			</Stack>
		</DialogTitle>
	);
};

export default AIAssistantDialogTitle;
